<template>
  <div class="adm-field" :class="{ 'is-invalid': !!error }">
    <label v-if="label" :for="fieldId" class="adm-field__label" :title="tooltip || undefined">
      {{ label }}
      <span v-if="required" class="adm-field__required" aria-hidden="true">*</span>
      <HelpCircle v-if="tooltip" :size="13" class="adm-field__help" aria-hidden="true" />
    </label>

    <!--
      El `select` usa `v-model` sobre un proxy en lugar de :value/@change.
      Motivo: `event.target.value` es SIEMPRE una cadena, asi que un
      `<option :value="prov.id">` con id numerico emitiria "3" en vez de 3,
      y `<option :value="null">` emitiria "null". Delegando en el v-model de
      Vue se conserva el valor original de cada opcion (numero, null u objeto),
      que es lo que esperan la API y las comparaciones de las vistas.
    -->
    <select
      v-if="type === 'select'"
      :id="fieldId"
      v-model="proxyValue"
      class="adm-field__control"
      :required="required"
      :disabled="disabled"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
    >
      <slot />
    </select>

    <textarea
      v-else-if="type === 'textarea'"
      :id="fieldId"
      class="adm-field__control adm-field__control--area"
      :value="modelValue"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :rows="rows"
      :aria-invalid="error ? 'true' : undefined"
      :aria-describedby="describedBy"
      @input="$emit('update:modelValue', $event.target.value)"
    ></textarea>

    <!-- El envoltorio sólo hace falta para poder colocar encima el ojo de
         mostrar/ocultar; para el resto de tipos no estorba. -->
    <div v-else class="adm-field__wrap">
      <input
        :id="fieldId"
        class="adm-field__control"
        :class="{ 'has-affix': isPassword }"
        :type="effectiveType"
        :value="modelValue"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :min="min"
        :max="max"
        :step="step"
        :maxlength="maxlength"
        :readonly="readonly"
        :list="list || undefined"
        :autocomplete="autocomplete || undefined"
        :aria-invalid="error ? 'true' : undefined"
        :aria-describedby="describedBy"
        @input="onInput"
      />
      <button
        v-if="isPassword"
        type="button"
        class="adm-field__eye"
        :aria-label="revealed ? 'Ocultar contraseña' : 'Mostrar contraseña'"
        :aria-pressed="revealed"
        @click="revealed = !revealed"
      >
        <component :is="revealed ? EyeOff : Eye" :size="17" />
      </button>
    </div>

    <p v-if="error" :id="`${fieldId}-error`" class="adm-field__error">{{ error }}</p>
    <p v-else-if="hint" :id="`${fieldId}-hint`" class="adm-field__hint">{{ hint }}</p>
  </div>
</template>

<script>
/**
 * Campo de formulario del panel — plan 27_09_2026 §4.2 (criterio C8).
 *
 * Los formularios administrativos heredaban de `global.css` un
 * `border: 4px solid #0B132A` pensado para los formularios grandes de la web
 * pública; en una pantalla de captura densa resultaba desproporcionado. Aquí
 * el borde es de 1px y el foco usa el teal de marca.
 *
 * Además resuelve lo que faltaba en casi todas las vistas: etiqueta asociada
 * al control por `for`/`id`, estado de error anunciado con `aria-invalid` y
 * texto de ayuda enlazado con `aria-describedby`.
 */
import { HelpCircle, Eye, EyeOff } from 'lucide-vue-next';

let uid = 0;

export default {
  name: 'AdmField',
  components: { HelpCircle },
  props: {
    // `null` y objetos incluidos: un `select` puede tener `:value="null"`.
    modelValue: { type: null, default: '' },
    label: { type: String, default: '' },
    /** 'text' | 'number' | 'date' | 'email' | 'password' | 'select' | 'textarea' … */
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: '' },
    hint: { type: String, default: '' },
    error: { type: String, default: '' },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    rows: { type: [String, Number], default: 4 },
    min: { type: [String, Number], default: undefined },
    max: { type: [String, Number], default: undefined },
    step: { type: [String, Number], default: undefined },
    maxlength: { type: [String, Number], default: undefined },
    id: { type: String, default: '' },
    /** Explicación larga al pasar el ratón por la etiqueta (campos de costeo). */
    tooltip: { type: String, default: '' },
    /** Valor calculado que se muestra pero no se edita (p. ej. costo/gramo). */
    readonly: { type: Boolean, default: false },
    /** id de un <datalist> con sugerencias. */
    list: { type: String, default: '' },
    autocomplete: { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  data() {
    return { generatedId: `adm-field-${++uid}`, revealed: false, Eye, EyeOff };
  },
  computed: {
    /** Los campos de contraseña traen su propio botón de mostrar/ocultar. */
    isPassword() {
      return this.type === 'password';
    },
    effectiveType() {
      return this.isPassword && this.revealed ? 'text' : this.type;
    },
    /** Puente entre el `v-model` del select y el del componente. */
    proxyValue: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    fieldId() {
      return this.id || this.generatedId;
    },
    describedBy() {
      if (this.error) return `${this.fieldId}-error`;
      if (this.hint) return `${this.fieldId}-hint`;
      return undefined;
    },
  },
  methods: {
    onInput(event) {
      const raw = event.target.value;
      // Los campos numéricos emiten número (o null si se vacían): las vistas
      // hacían `Number(...)` a mano en cada `@input` y algunas lo olvidaban.
      if (this.type === 'number') {
        this.$emit('update:modelValue', raw === '' ? null : Number(raw));
        return;
      }
      this.$emit('update:modelValue', raw);
    },
  },
};
</script>

<style scoped>
.adm-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;
}

.adm-field__label {
  font-family: var(--font-body);
  font-size: var(--adm-font-sm);
  font-weight: 600;
  color: var(--color-text-strong);
}

.adm-field__required { color: var(--adm-danger-fg); margin-left: 0.15rem; }

.adm-field__help {
  vertical-align: -1px;
  margin-left: 0.2rem;
  color: var(--adm-border-strong);
}

.adm-field__wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.adm-field__eye {
  position: absolute;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  background: none;
  border: none;
  border-radius: var(--adm-radius-sm);
  color: var(--color-text);
  cursor: pointer;
}

.adm-field__eye:hover { color: var(--color-brand); }

/* Deja sitio al ojo para que no tape el texto escrito. */
.adm-field__control.has-affix { padding-right: 2.4rem; }

.adm-field__control {
  width: 100%;
  min-height: var(--adm-control-height);
  /* `global.css` da `margin-bottom: var(--space-sm)` a todo `form input`,
     pensado para los formularios en columna de la web pública. Dentro del
     panel el espaciado lo pone la rejilla, así que se anula aquí. */
  margin: 0;
  padding: 0.45rem 0.6rem;
  background-color: var(--adm-surface);
  border: 1px solid var(--adm-border-strong);
  border-radius: var(--adm-radius-sm);
  box-sizing: border-box;
  font-family: var(--font-body);
  /* 16px evita el zoom automático de iOS al enfocar (mismo criterio que
     `global.css` tras el plan 25_08_2026). */
  font-size: 16px;
  color: var(--color-text-strong);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.adm-field__control:focus {
  outline: none;
  border-color: var(--color-brand);
  box-shadow: 0 0 0 3px rgba(46, 138, 153, 0.18);
}

.adm-field__control:disabled {
  background-color: var(--adm-surface-alt);
  color: var(--color-text);
  cursor: not-allowed;
}

.adm-field__control::placeholder { color: var(--adm-placeholder); }

.adm-field__control[readonly] {
  background-color: var(--adm-surface-alt);
  color: var(--color-text);
  cursor: default;
}

.adm-field__control--area {
  min-height: 6rem;
  resize: vertical;
  line-height: 1.4;
}

.adm-field.is-invalid .adm-field__control { border-color: var(--adm-danger-fg); }

.adm-field.is-invalid .adm-field__control:focus {
  box-shadow: 0 0 0 3px rgba(183, 28, 28, 0.15);
}

.adm-field__error,
.adm-field__hint {
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.3;
}

.adm-field__error { color: var(--adm-danger-fg); }
.adm-field__hint { color: var(--color-text); }

@media (min-width: 768px) {
  /* En escritorio el zoom de iOS no aplica y 16px resulta grande para una
     rejilla densa de captura. */
  .adm-field__control { font-size: var(--adm-font-base); }
}
</style>
